import { useState, useEffect, useCallback, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { getAuditLogs, AuditLog, AuditLogsFilters } from '../services/auditLogsService';
import '../styles/ListPages.css';
import '../styles/FormPages.css';

export function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedLog, setExpandedLog] = useState<number | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(50);

  // Filter state
  const [filters, setFilters] = useState<AuditLogsFilters>({
    actionType: '',
    resourceType: '',
    startDate: '',
    endDate: '',
    success: undefined
  });

  // Available options for filters
  const actionTypes = [
    'LOGIN', 'LOGOUT', 'LOGIN_FAILED', 'CREATE', 'READ', 'UPDATE', 'DELETE',
    'SEARCH', 'PASSWORD_CHANGE', 'PASSWORD_RESET', 'EXPORT', 'DOWNLOAD', 'PRINT'
  ];

  const resourceTypes = [
    'Individual', 'CANS', 'ContactLog', 'Family', 'FamilyMember',
    'Donor', 'Donation', 'Staff', 'Configuration', 'Authentication'
  ];

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // Build filters object with pagination
      const requestFilters: AuditLogsFilters = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      };

      // Add filters only if they have values
      if (filters.actionType) {requestFilters.actionType = filters.actionType;}
      if (filters.resourceType) {requestFilters.resourceType = filters.resourceType;}
      if (filters.startDate) {requestFilters.startDate = filters.startDate;}
      if (filters.endDate) {requestFilters.endDate = filters.endDate;}
      if (filters.success !== undefined) {requestFilters.success = filters.success;}

      const response = await getAuditLogs(requestFilters);
      setLogs(response.logs);
      setTotalCount(response.pagination.total);
    } catch (err) {
      setError('Failed to load audit logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, pageSize]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;

    setFilters(prev => ({
      ...prev,
      [name]: name === 'success' && value === '' ? undefined : value
    }));

    // Reset to page 1 when filters change
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      actionType: '',
      resourceType: '',
      startDate: '',
      endDate: '',
      success: undefined
    });
    setCurrentPage(1);
  };

  const toggleExpandLog = (logId: number) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDetails = (details: string | null) => {
    if (!details) {return 'N/A';}
    try {
      const parsed = JSON.parse(details);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return details;
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && logs.length === 0) {
    return (
      <div className="main-container">
        <div className="page-header">
          <h1>Audit Logs</h1>
        </div>
        <div className="loading-container">Loading audit logs...</div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="page-header">
        <h1>Audit Logs</h1>
        <Link to="/admin" className="btn-secondary">
          Back to Admin
        </Link>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Filters Section */}
      <div className="filters-section" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        <h3 style={{ marginTop: 0 }}>Filters</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <label htmlFor="actionType" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Action Type
            </label>
            <select
              id="actionType"
              name="actionType"
              value={filters.actionType}
              onChange={handleFilterChange}
              style={{ width: '100%', padding: '8px' }}
            >
              <option value="">All Actions</option>
              {actionTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="resourceType" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Resource Type
            </label>
            <select
              id="resourceType"
              name="resourceType"
              value={filters.resourceType}
              onChange={handleFilterChange}
              style={{ width: '100%', padding: '8px' }}
            >
              <option value="">All Resources</option>
              {resourceTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="success" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Status
            </label>
            <select
              id="success"
              name="success"
              value={filters.success === undefined ? '' : filters.success.toString()}
              onChange={handleFilterChange}
              style={{ width: '100%', padding: '8px' }}
            >
              <option value="">All</option>
              <option value="true">Success</option>
              <option value="false">Failed</option>
            </select>
          </div>

          <div>
            <label htmlFor="startDate" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Start Date
            </label>
            <input
              type="datetime-local"
              id="startDate"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              style={{ width: '100%', padding: '8px' }}
            />
          </div>

          <div>
            <label htmlFor="endDate" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              End Date
            </label>
            <input
              type="datetime-local"
              id="endDate"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              style={{ width: '100%', padding: '8px' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button onClick={clearFilters} className="btn-secondary" style={{ width: '100%' }}>
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
        Showing {logs.length} of {totalCount} total logs
        {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
      </div>

      {/* Audit Logs Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '160px' }}>Timestamp</th>
              <th style={{ width: '100px' }}>Action</th>
              <th style={{ width: '100px' }}>Resource</th>
              <th style={{ width: '80px' }}>Resource ID</th>
              <th>User</th>
              <th style={{ width: '80px' }}>Role</th>
              <th style={{ width: '120px' }}>IP Address</th>
              <th style={{ width: '80px' }}>Status</th>
              <th style={{ width: '80px' }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  No audit logs found
                </td>
              </tr>
            ) : (
              logs.map(log => (
                <Fragment key={log.auditLogID}>
                  <tr>
                    <td style={{ fontSize: '12px' }}>{formatTimestamp(log.eventTimestamp)}</td>
                    <td>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '3px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          backgroundColor: log.actionType.includes('DELETE') ? '#ffebee' :
                                         log.actionType.includes('CREATE') ? '#e8f5e9' :
                                         log.actionType.includes('UPDATE') ? '#fff3e0' :
                                         log.actionType.includes('LOGIN') ? '#e3f2fd' : '#f5f5f5',
                          color: log.actionType.includes('DELETE') ? '#c62828' :
                                log.actionType.includes('CREATE') ? '#2e7d32' :
                                log.actionType.includes('UPDATE') ? '#ef6c00' :
                                log.actionType.includes('LOGIN') ? '#1565c0' : '#424242'
                        }}
                      >
                        {log.actionType}
                      </span>
                    </td>
                    <td>{log.resourceType}</td>
                    <td>{log.resourceID ?? '-'}</td>
                    <td>
                      <div style={{ fontSize: '13px' }}>
                        <strong>{log.userName || log.userEmail}</strong>
                        {log.userName && (
                          <>
                            <br />
                            <span style={{ fontSize: '11px', color: '#666' }}>{log.userEmail}</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td>{log.userRole}</td>
                    <td style={{ fontSize: '11px' }}>{log.ipAddress || '-'}</td>
                    <td>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '3px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          backgroundColor: log.success ? '#e8f5e9' : '#ffebee',
                          color: log.success ? '#2e7d32' : '#c62828'
                        }}
                      >
                        {log.success ? 'Success' : 'Failed'}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => toggleExpandLog(log.auditLogID)}
                        className="btn-secondary"
                        style={{ padding: '4px 8px', fontSize: '11px' }}
                      >
                        {expandedLog === log.auditLogID ? 'Hide' : 'Show'}
                      </button>
                    </td>
                  </tr>
                  {expandedLog === log.auditLogID && (
                    <tr>
                      <td colSpan={9} style={{ backgroundColor: '#fafafa', padding: '15px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '13px' }}>
                          <div>
                            <strong>Request Method:</strong> {log.requestMethod || 'N/A'}
                          </div>
                          <div>
                            <strong>Request URL:</strong> {log.requestURL || 'N/A'}
                          </div>
                          <div>
                            <strong>User Agent:</strong> {log.userAgent || 'N/A'}
                          </div>
                          {log.errorMessage && (
                            <div style={{ color: '#c62828' }}>
                              <strong>Error Message:</strong> {log.errorMessage}
                            </div>
                          )}
                          {log.details && (
                            <div style={{ gridColumn: '1 / -1' }}>
                              <strong>Additional Details:</strong>
                              <pre style={{
                                backgroundColor: '#fff',
                                padding: '10px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                marginTop: '5px',
                                fontSize: '11px',
                                overflow: 'auto',
                                maxHeight: '200px'
                              }}>
                                {formatDetails(log.details)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination" style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="btn-secondary"
            style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
          >
            Previous
          </button>

          <span style={{ padding: '8px 16px', alignSelf: 'center' }}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="btn-secondary"
            style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
